// @flow
import React from 'react';
import PropTypes from 'prop-types';
import {Divider, Typography} from '@material-ui/core';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import makeStyles from '@material-ui/core/styles/makeStyles';

import CustomModal from '../../../../elements/CustomModal/CustomModal';
import type {Order} from '../../../../entities';
import {colors} from '../../../../assets/styles/colors';
import type {OrderStockError} from '../../duck';
import {useMemo} from 'react';
import {ArrowForward} from '@material-ui/icons';

type Props = {
  open: boolean,
  handleClose: () => void,
  order: Order,
  orderStockErrors: OrderStockError[],
  handleConfirm: () => void,
};
const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: 340,
  },
  title: {
    fontSize: '1.25rem',
    fontWeight: 600,
    marginTop: theme.spacing(2),
  },
  divider: {
    background: colors.grey200,
    width: '100%',
    margin: theme.spacing(2, 0),
  },
  errorsArea: {
    width: '100%',
  },
  orderStockError: {
    display: 'flex',
    alignItems: 'center',
    '&:not(:first-child)': {
      marginTop: theme.spacing(2),
    },
  },
  product: {
    width: 200,
  },
  productName: {
    fontSize: '0.875rem',
    color: colors.grey800,
    marginBottom: 4,
  },
  productVariant: {
    fontSize: '0.75rem',
    color: colors.grey800,
  },
  productLinkButton: {
    margin: '-6px -4px',
    fontSize: '0.75rem',
  },
  productError: {
    width: 140,
    fontWeight: 700,
    textAlign: 'center',
  },
  tip: {
    marginBottom: theme.spacing(2),
    color: colors.grey700,
    fontSize: '0.75rem',
  },
  button: {
    height: 40,
    fontSize: '1rem',
    alignSelf: 'stretch',
    marginBottom: theme.spacing(1),
  },
}));

export default function EditOrderProductsErrorsModal(props: Props) {
  const {open, order, orderStockErrors, handleConfirm, handleClose} = props;
  const classes = useStyles();

  const hasNoStockError = useMemo(() => {
    if (
      orderStockErrors &&
      orderStockErrors.find(e =>
        [
          'NOT_ENOUGH_PRODUCT_STOCK_ERROR',
          'NOT_ENOUGH_PRODUCT_VARIANT_STOCK_ERROR',
        ].includes(e.error)
      )
    )
      return true;
  }, [orderStockErrors]);

  const processedStockErrors = useMemo(() => {
    if (orderStockErrors) {
      return orderStockErrors.map(error => {
        if (
          !hasNoStockError ||
          (hasNoStockError &&
            [
              'NOT_ENOUGH_PRODUCT_STOCK_ERROR',
              'NOT_ENOUGH_PRODUCT_VARIANT_STOCK_ERROR',
            ].includes(error.error))
        ) {
          let processedError = {};
          const product = order.items.find(item =>
            error.productVariantId && item.productVariantId
              ? error.productVariantId === item.productVariantId
              : item.productId === error.productId
          );
          if (product) {
            processedError.productName = product.name;
            processedError.productId = error.productId;
            if (product.productVariant)
              processedError.productVariant = product.productVariant;
          }

          processedError.productError = {
            PRODUCT_NOT_FOUND_ERROR: 'Producto no existe*',
            PRODUCT_VARIANT_NOT_FOUND_ERROR: 'Variante no existe*',
            NOT_ENOUGH_PRODUCT_STOCK_ERROR:
              typeof error.availableStock === 'number'
                ? `${error.availableStock} und`
                : '',
            NOT_ENOUGH_PRODUCT_VARIANT_STOCK_ERROR:
              typeof error.availableStock === 'number'
                ? `${error.availableStock} und`
                : '',
            PRODUCT_VARIANT_IS_DISABLED: 'Variantes deshabilitadas',
            PRODUCT_VARIANT_NOT_PROVIDED_ERROR: 'Variante no seleccionada*',
          }[error.error];

          return processedError;
        }
        return null;
      });
    }
  }, [hasNoStockError, order.items, orderStockErrors]);

  const explanationNotFound = useMemo(() => {
    if (
      orderStockErrors &&
      orderStockErrors.find(e =>
        ['PRODUCT_NOT_FOUND_ERROR', 'PRODUCT_VARIANT_NOT_FOUND_ERROR'].includes(
          e.error
        )
      )
    )
      return true;
  }, [orderStockErrors]);

  const explanationVariantNotProvided = useMemo(() => {
    return (
      orderStockErrors &&
      !!orderStockErrors.find(e =>
        ['PRODUCT_VARIANT_NOT_PROVIDED_ERROR'].includes(e.error)
      )
    );
  }, [orderStockErrors]);

  return (
    <CustomModal open={open} handleCloseModal={handleClose}>
      {hasNoStockError ? (
        <div className={classes.root}>
          <Typography className={classes.title} align="center">
            Los siguientes productos no tienen suficiente stock:
          </Typography>
          <Divider className={classes.divider} />
          <div className={classes.errorsArea}>
            {processedStockErrors &&
              processedStockErrors.map(
                (error, i) =>
                  error && (
                    <div key={`error-${i}`} className={classes.orderStockError}>
                      <div className={classes.product}>
                        <Typography className={classes.productName}>
                          {error.productName}
                        </Typography>
                        {error.productVariant &&
                          Object.keys(error.productVariant).map(criteria => (
                            <Typography
                              key={`error-${i}-${criteria}`}
                              className={classes.productVariant}
                            >
                              {`${criteria}: ${error.productVariant[criteria]}`}
                            </Typography>
                          ))}
                        <CustomButton
                          color="primary"
                          size="small"
                          className={classes.productLinkButton}
                          endIcon={<ArrowForward style={{marginLeft: -4}} />}
                          to={`/products/${error.productId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Ir a editar producto
                        </CustomButton>
                      </div>
                      <Typography className={classes.productError}>
                        {error.productError}
                      </Typography>
                    </div>
                  )
              )}
          </div>
          <Divider className={classes.divider} />
          <Typography className={classes.tip}>
            Para continuar, por favor regularizar stocks del producto.
          </Typography>
          <CustomButton
            className={classes.button}
            variant="contained"
            onClick={handleConfirm}
            // disabled={isSaving}
          >
            Continuar
          </CustomButton>
          <CustomButton
            className={classes.button}
            onClick={handleClose}
            // disabled={isSaving}
          >
            Cerrar
          </CustomButton>
        </div>
      ) : (
        <div className={classes.root}>
          <Typography className={classes.title} align="center">
            Los siguientes productos presentan inconsistencias:
          </Typography>
          <Divider className={classes.divider} />
          <div>
            {processedStockErrors &&
              processedStockErrors.map(
                (error, i) =>
                  error && (
                    <div key={`error-${i}`} className={classes.orderStockError}>
                      <div className={classes.product}>
                        <Typography className={classes.productName}>
                          {error.productName}
                        </Typography>
                        {error.productVariant &&
                          Object.keys(error.productVariant).map(criteria => (
                            <Typography
                              key={`error-${i}-${criteria}`}
                              className={classes.productVariant}
                            >
                              {`${criteria}: ${error.productVariant[criteria]}`}
                            </Typography>
                          ))}
                      </div>
                      <Typography className={classes.productError}>
                        {error.productError}
                      </Typography>
                    </div>
                  )
              )}
          </div>
          <Divider className={classes.divider} />
          {explanationNotFound && (
            <Typography className={classes.tip}>
              *Producto o variante de producto se ha eliminado de la plataforma
              o desactivado. Si continúa, no se reducirá el stock de estos
              productos.
            </Typography>
          )}
          {explanationVariantNotProvided && (
            <Typography className={classes.tip}>
              *Producto no tiene ninguna variante seleccionada. Si continúa, no
              se reducirá el stock de estos productos.
            </Typography>
          )}
          <CustomButton
            className={classes.button}
            variant="contained"
            onClick={handleConfirm}
            // disabled={isSaving}
          >
            Continuar
          </CustomButton>
          <CustomButton className={classes.button} onClick={handleClose}>
            Cancelar
          </CustomButton>
        </div>
      )}
    </CustomModal>
  );
}

EditOrderProductsErrorsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  updateOrderStateRequest: PropTypes.object,
  orderStockErrors: PropTypes.array,
  handleConfirm: PropTypes.func.isRequired,
  order: PropTypes.object.isRequired,
};
